import { ref } from 'vue'
import { withTokenRetry } from '../utils/api-helpers.js'

// Ticket type constants
export const TICKET_TYPES = {
  MEMBERSHIP: 'membership',
  VIP: 'vip',
  PARKING: 'parking',
  CHILDREN: 'children',
  BULK: 'bulk',
} as const

// Type definitions for ticket types
export interface TicketTypeInfo {
  label: string
  description: string
  maxPerMember: number | null
  requiredMemberType: string | null
  requiresMembership: boolean
  buttonLabel: string
  buttonSeverity: string
}

// Interfaces for Eventbrite API
export interface EventbriteAttendee {
  id: string
  ticket_class_id: string
  quantity: number
  profile: {
    name: string
    email: string
    [key: string]: unknown
  }
  barcodes?: Array<{
    barcode: string
    status: string
    [key: string]: unknown
  }>
  checked_in?: boolean
  cancelled?: boolean
  refunded?: boolean
  status?: string
  [key: string]: unknown
}

export interface EventbriteTicketClass {
  id: string
  name: string
  description: string | null
  cost: {
    currency: string
    value: number
    display: string
  }
  free: boolean
  minimum_quantity: number
  maximum_quantity: number
  quantity_total: number
  quantity_sold: number
  sales_start: string
  sales_end: string
  hidden: boolean
  include_fee: boolean
  split_fee: boolean
  sales_status: string
  ticket_type?: string
}

export interface EventbriteOrderResponse {
  id: string
  name?: string
  attendees?: EventbriteAttendee[]
  ticket_classes?: Array<{
    id: string
    name: string
    quantity: number
    [key: string]: unknown
  }>
  [key: string]: unknown
}

export interface EventbriteOrderData {
  orderId: string
  orderNumber?: string
  purchaserName?: string
  purchaserEmail?: string
  status?: string
  ticketId?: string
  ticketClassName?: string
  quantity?: number
  [key: string]: unknown
}

export interface OrderResponse {
  orderId: string
}

export interface EBWidgetType {
  createWidget: (options: {
    widgetType: string
    eventId: string
    promoCode?: string
    iframeContainerId: string
    iframeContainerHeight: number
    onOrderComplete?: (orderId: OrderResponse) => void
  }) => void
}

// Extend the Window interface
declare global {
  interface Window {
    EBWidgets?: EBWidgetType
  }
}

// Ticket type information for UI and validation
export const TICKET_TYPE_INFO: Record<string, TicketTypeInfo> = {
  [TICKET_TYPES.MEMBERSHIP]: {
    label: 'Membership',
    description: 'Annual membership dues for participation in the parade',
    maxPerMember: 1,
    requiredMemberType: null,
    requiresMembership: false,
    buttonLabel: 'Pay Dues',
    buttonSeverity: 'primary',
  },
  [TICKET_TYPES.VIP]: {
    label: 'VIP Upgrade',
    description: 'Access to the VIP room at the Chewbacchanal after party',
    maxPerMember: 1,
    requiredMemberType: null,
    requiresMembership: true,
    buttonLabel: 'Chewbacchanal VIP Upgrade',
    buttonSeverity: 'success',
  },
  [TICKET_TYPES.PARKING]: {
    label: 'Contraption Parking',
    description: 'Overnight parking for contraptions after the parade',
    maxPerMember: null,
    requiredMemberType: null,
    requiresMembership: true,
    buttonLabel: 'Buy Contraption Parking',
    buttonSeverity: 'info',
  },
  [TICKET_TYPES.CHILDREN]: {
    label: 'Children\'s Ticket',
    description: 'For children to join the parade (not official members)',
    maxPerMember: null,
    requiredMemberType: null,
    requiresMembership: true,
    buttonLabel: 'Pay Children\'s Ticket',
    buttonSeverity: 'secondary',
  },
  [TICKET_TYPES.BULK]: {
    label: 'Bulk Memberships',
    description: 'Buy multiple memberships for your SubKrewe with optional discount',
    maxPerMember: null,
    requiredMemberType: 'captain',
    requiresMembership: true,
    buttonLabel: 'Buy Multiple Memberships',
    buttonSeverity: 'warning',
  },
}

/**
 * Composable for interacting with the Eventbrite API
 * Provides methods for fetching tickets, generating access codes, and creating purchase URLs
 */
export const useEventbriteService = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Get available tickets by type (membership, vip, parking, etc.)
   */
  const getInventoryTiersByType = async (type: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await withTokenRetry(async () => {
        return await $fetch(`/api/eventbrite/inventory-tiers?type=${type}`, {
          method: 'GET',
        })
      })

      if (response.error) {
        throw new Error(response.message || 'Failed to fetch tickets')
      }

      // Return with proper structure indicating availability
      return {
        inventoryTiers: response.inventory_tiers,
      }
    }
    catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch tickets'
      return {
        available: false,
        error: true,
        message: error.value,
      }
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Generate an access code for a specific ticket class or inventory tier
   */
  const generateAccessCode = async (options: { ticketClassId?: string, inventoryTierId?: string }) => {
    isLoading.value = true
    error.value = null

    if (!options.ticketClassId && !options.inventoryTierId) {
      error.value = 'Either ticketClassId or inventoryTierId is required'
      return {
        success: false,
        error: true,
        message: error.value,
      }
    }

    try {
      const response = await withTokenRetry(async () => {
        return await $fetch('/api/eventbrite/access-code', {
          method: 'POST',
          body: options,
        })
      })

      if (response.error) {
        throw new Error(response.message || 'Failed to generate access code')
      }

      return {
        success: true,
        code: response.code,
      }
    }
    catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to generate access code'
      return {
        success: false,
        error: true,
        message: error.value,
      }
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Get a ticket purchase URL with an access code
   */
  const getTicketPurchaseUrl = (accessCode: string) => {
    const eventId = process.env.EVENTBRITE_EVENT_ID || ''
    const config = useRuntimeConfig()
    // Use krewebacchusEnv for reliable environment detection
    const env = (config.public?.krewebacchusEnv || 'dev').toString().toLowerCase()
    const isQAMode = env !== 'production' && env !== 'prod'

    // Use the QA URL for development, production URL otherwise
    const baseUrl = isQAMode
      ? 'https://www.evbqa.com/e'
      : 'https://www.eventbrite.com/e'

    return `${baseUrl}/${eventId}?access=${accessCode}`
  }

  /**
   * Generate a checkout session for a specific ticket type
   * @param ticketType The type of ticket to purchase (membership, vip, parking, children)
   * @param options Optional parameters (child-specific IDs, quantity)
   * @returns Promise with access code information
   */
  const generateCheckoutSession = async (
    ticketType: typeof TICKET_TYPES[keyof typeof TICKET_TYPES],
    options: {
      childId?: string
      childrenIds?: string[]
      quantity?: number
      subkreweId?: string
    } = {},
  ): Promise<{ success: boolean, code?: string, message?: string, accessUrl?: string }> => {
    isLoading.value = true
    error.value = null

    try {
      // Validate ticket type
      if (!Object.values(TICKET_TYPES).includes(ticketType)) {
        throw new Error(`Invalid ticket type: ${ticketType}`)
      }

      // For child tickets, ensure we have either childId, childrenIds, or quantity
      if (ticketType === TICKET_TYPES.CHILDREN) {
        if (!options.childId && !options.childrenIds && !options.quantity) {
          throw new Error('Child ticket purchases require childId, childrenIds, or quantity')
        }

        // If childrenIds is provided, calculate quantity from it
        if (options.childrenIds && options.childrenIds.length > 0 && !options.quantity) {
          options.quantity = options.childrenIds.length
        }
      }

      // Bulk uses a different endpoint and payload
      let response: { success?: boolean, accessCode?: string, accessCodeUrl?: string, code?: string } | undefined
      if (ticketType === TICKET_TYPES.BULK) {
        if (!options.subkreweId) throw new Error('subkreweId is required for bulk purchases')
        response = await withTokenRetry(async () => {
          return await $fetch('/api/eventbrite/bulk-access-code', {
            method: 'POST',
            body: { subkreweId: options.subkreweId },
          })
        })
      }
      else {
        // Build API request body for regular types
        const requestBody = {
          type: ticketType,
          // Include optional parameters when provided
          ...(options.childId && { childId: options.childId }),
          ...(options.childrenIds && { childrenIds: options.childrenIds }),
          ...(options.quantity && { quantity: options.quantity }),
        }
        response = await withTokenRetry(async () => {
          return await $fetch('/api/eventbrite/access-code', {
            method: 'POST',
            body: requestBody,
          })
        })
      }

      if (!response || response.success === false) {
        throw new Error('Failed to create checkout session')
      }

      const code = (response?.code ?? response?.accessCode) as string | undefined
      return { success: true, code, accessUrl: response?.accessCodeUrl }
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred'
      return {
        success: false,
        message: error.value,
      }
    }
    finally {
      isLoading.value = false
    }
  }

  // Return the public API
  return {
    isLoading,
    error,
    getInventoryTiersByType,
    generateAccessCode,
    getTicketPurchaseUrl,
    generateCheckoutSession,
  }
}
